import { Icon, IconName } from '../../foundations';
import { Text } from '../../foundations';
import clsx from 'clsx';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import styles from './DialogIberia.module.css';
import { Divider } from '../../layout';
import { Button } from '../button';
import { As } from '../../foundations/text/Text';

export const DialogIberiaInteractionType = {
	PRIMARY: 'PRIMARY',
	SECONDARY: 'SECONDARY',
	CANCEL: 'CANCEL',
	CLOSE: 'CLOSE'
} as const;
export type DialogIberiaInteractionType =
	(typeof DialogIberiaInteractionType)[keyof typeof DialogIberiaInteractionType];

export type DialogIberiaProps = {
	/**
	 * Show accessibility information for the close button, needed to show the button
	 */
	ariaLabelClose?: string;
	/**
	 * If the modal is scrollable (True by default) or should have overflow hidden
	 */
	scrollable?: boolean;
	/**
	 * Main title of the modal. Needed for accessibility reasons.
	 */
	header?: React.ReactNode;
	/**
	 * Subtitle of the modal. It will be shown in the header
	 */
	subtitle?: React.ReactNode;
	/**
	 * Name of the icon to be shown or alternatively an Icon component, it will be styled accordingly
	 */
	icon?: React.ReactElement<React.ComponentProps<typeof Icon>> | IconName;
	/**
	 * Bold body entrance
	 */
	entrance?: React.ReactNode;
	/**
	 * Body text, preferable to use children for a customizable option
	 */
	body?: React.ReactNode;
	/**
	 * Body text, preferable to use children for a customizable option
	 */
	bodyAs?: As;
	/**
	 * Cancel button text, the button will be shown
	 */
	cancelButton?: React.ReactNode;
	/**
	 * Primary button text at bottom, the button will be shown
	 */
	primaryButton?: React.ReactNode;
	/**
	 * Secondary button text at bottom, the button will be shown
	 */
	secondaryButton?: React.ReactNode;
	/**
	 * General body of the modal
	 */
	children?: React.ReactNode;

	/**
	 * This callback function will be called whenever the user interacts with any
	 * of the available button. Sending a type that identifies what interaction the user
	 * have used.
	 */
	onUserInteraction?: (type: DialogIberiaInteractionType) => void;
};

type BasicModalFooterProps = {
	cancelButton?: React.ReactNode;
	primaryButton?: React.ReactNode;
	secondaryButton?: React.ReactNode;
	buttonClassName?: string;
	onUserInteraction?: (type: DialogIberiaInteractionType) => void;
};

type BasicModalHeaderProps = {
	header?: React.ReactNode;
	subtitle?: React.ReactNode;
	icon?: React.ReactElement<React.ComponentProps<typeof Icon>> | string;
};

const getNumber = (property?: React.ReactNode) => {
	return property ? 1 : 0;
};

/**
 * To be used internally to generate the Iberia Dialog Modal
 */
export function IbReactModal({
	ariaLabelClose,
	scrollable = true,
	header,
	subtitle,
	icon,
	entrance,
	body,
	bodyAs,
	cancelButton,
	primaryButton,
	secondaryButton,
	children,
	onUserInteraction
}: DialogIberiaProps) {
	return (
		<section className={clsx(styles.modal)}>
			{ariaLabelClose && (
				<DialogPrimitive.Close
					aria-label={ariaLabelClose}
					className={styles.close}
					onClick={() => {
						onUserInteraction?.(DialogIberiaInteractionType.CLOSE);
					}}
				>
					<Icon name="close" />
				</DialogPrimitive.Close>
			)}
			{header && <BasicModalHeader header={header} subtitle={subtitle} icon={icon} />}
			{header && <Divider className={styles.dividerContainer} variant="solid"></Divider>}
			<div className={clsx(styles.body, { [styles.scroll]: scrollable })}>
				{entrance && (
					<Text className={styles.textContainer} font="verdana" size="md-bold">
						{entrance}
					</Text>
				)}
				{body && (
					<Text as={bodyAs} className={styles.textContainer} font="verdana" size="lg-regular">
						{body}
					</Text>
				)}
				{children}
			</div>
			<BasicModalFooter
				cancelButton={cancelButton}
				primaryButton={primaryButton}
				secondaryButton={secondaryButton}
				onUserInteraction={onUserInteraction}
			/>
		</section>
	);
}

export function BasicModalFooter({
	cancelButton,
	primaryButton,
	secondaryButton,
	onUserInteraction
}: BasicModalFooterProps) {
	const totalButtons = getNumber(cancelButton) + getNumber(secondaryButton) + getNumber(primaryButton);

	return (
		<footer className={clsx(styles.buttons, styles[`grid${String(totalButtons)}`])}>
			{cancelButton && (
				<Button
					className={clsx(styles.button, styles.buttonsCancel)}
					buttonSize="big"
					full
					onClick={() => {
						onUserInteraction?.('CANCEL');
					}}
					variant="text"
				>
					{cancelButton}
				</Button>
			)}
			{secondaryButton && (
				<Button
					className={clsx(styles.button, styles.buttonsMiddle)}
					buttonSize="big"
					onClick={() => {
						onUserInteraction?.('SECONDARY');
					}}
					full
					variant="secondary"
				>
					{secondaryButton}
				</Button>
			)}
			{primaryButton && (
				<Button
					className={clsx(styles.button, styles.buttonsRight, styles[`grid${String(totalButtons)}`])}
					buttonSize="big"
					data-testid="primary-button"
					onClick={() => {
						onUserInteraction?.(DialogIberiaInteractionType.PRIMARY);
					}}
					full
				>
					{primaryButton}
				</Button>
			)}
		</footer>
	);
}

export function BasicModalHeader({ header, subtitle, icon }: BasicModalHeaderProps) {
	return (
		<header className={styles.header}>
			{icon && (
				<div className={styles.headerIcon}>{typeof icon === 'string' ? <Icon name={icon as IconName} /> : icon}</div>
			)}
			<div className={styles.headerText}>
				<DialogPrimitive.Title asChild>
					<Text as={'h2'} font="iberia-headline">
						{header}
					</Text>
				</DialogPrimitive.Title>
				{subtitle && (
					<DialogPrimitive.Description asChild>
						<Text as={'p'} font="iberia-headline" size="xs-regular">
							{subtitle}
						</Text>
					</DialogPrimitive.Description>
				)}
			</div>
		</header>
	);
}
